import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FlightIcon from '@mui/icons-material/Flight';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import SectionHeader from 'components/Common/SectionHeader';
import ConnectForm from 'components/RouteWizard/ConnectForm';
import Input from 'components/RouteWizard/Input';
import { Select } from 'components/RouteWizard/Select';
import useFlightLookup from 'hooks/useFlightLookup';
import { DateTime } from 'luxon';
import { useFormContext, useWatch } from 'react-hook-form';

const formatFlightTime = (
  iso: string | undefined,
  timeZone: string | undefined,
): string => {
  if (!iso) return ''
  const dt = DateTime.fromISO(iso, { setZone: true })
  if (!dt.isValid) return iso
  return `${dt.toFormat('HH:mm')} (${timeZone?.replace(/_/g, ' ') ?? ''})`
}

type AdditionalServicesProps = {
  namePrefix: string
  disabled?: boolean
}

const looksLikeAirport = (location: any): boolean => {
  if (!location) return false
  if (location?.pointOfInterestTypeName === 'Airport') return true
  if (location?.id?.pointOfInterestTypeName === 'Airport') return true
  const addr = (
    location?.formattedAddress ||
    location?.id?.formattedAddress ||
    ''
  ).toLowerCase()
  return /airport|\([a-z]{3}\)/i.test(addr)
}

const AdditionalServices = ({
  namePrefix,
  disabled = false,
}: AdditionalServicesProps) => {
  const { control } = useFormContext()
  const pickup = useWatch({ control, name: 'outbound.pickup' })
  const dropOff = useWatch({ control, name: 'outbound.dropOff' })
  const flightNumber = useWatch({
    control,
    name: `${namePrefix}.flightDetails.flightNumber`,
  })
  const outboundWhen = useWatch({ control, name: `${namePrefix}.when` })

  const isNearAirport = looksLikeAirport(pickup) || looksLikeAirport(dropOff)

  // Pickup is airport > arrival; dropoff is airport > departure
  const isArrival = looksLikeAirport(pickup)

  // Use local date (not UTC) to avoid day-shift when local time is ahead/behind UTC
  const toLocalDateStr = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate(),
    ).padStart(2, '0')}`

  const dateStr =
    outboundWhen instanceof Date
      ? toLocalDateStr(outboundWhen)
      : typeof outboundWhen === 'string'
      ? outboundWhen.slice(0, 10)
      : undefined

  // Flight lookup fires whenever a flight number is entered — not just at an
  // airport. The airport-specific prompt below (and `isArrival` below) still
  // use `isNearAirport`, but the underlying lookup is always on so users can
  // track a flight even when pickup/dropoff isn't the airport itself.
  const { flight, isLoading: flightLoading } = useFlightLookup({
    flightNumber,
    date: dateStr,
    isArrival,
    enabled: !!flightNumber?.trim(),
  })

  return (
    <ConnectForm>
      <Box
        component="div"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: '1',
          gap: '15px',
          marginTop: '20px',
        }}
      >
        <SectionHeader
          title="Additional services"
          containerStyles={{ marginBottom: '0' }}
        />
        <Select
          name={`${namePrefix}.services`}
          options={{
            None: 'Not Required',
            MeetAndGreet: 'Yes - Meet and Greet',
          }}
          label="Meet and Greet?"
          disabled={disabled}
        />

        <Box sx={{ position: 'relative' }}>
          <Input
            name={`${namePrefix}.flightDetails.flightNumber`}
            label="Flight Number"
            disabled={disabled}
            inputProps={{ maxLength: 2000 }}
            sx={{ width: '100%' }}
          />
          {isNearAirport && !flight && !flightLoading && (
            <Alert
              icon={<FlightIcon fontSize="small" />}
              severity="info"
              sx={{ fontSize: '13px', py: 0.5 }}
            >
              Let us know your flight number so we can track your booking
            </Alert>
          )}
          {flightLoading && (
            <CircularProgress
              size={16}
              sx={{
                position: 'absolute',
                right: 12,
                top: '50%',
                transform: 'translateY(-50%)',
              }}
            />
          )}
        </Box>
        {flight && !flightLoading && (
          <Alert
            icon={<CheckCircleIcon fontSize="small" />}
            severity="success"
            sx={{ fontSize: '13px', py: 0.5 }}
          >
            <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
              {flight.departure?.iata} {'>'} {flight.arrival?.iata}
              {flight.status ? ` · ${flight.status}` : ''}
            </Typography>
            {isArrival && flight.arrival?.terminal && (
              <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                Terminal {flight.arrival.terminal}
                {flight.arrivalTime
                  ? ` · arrives ${formatFlightTime(
                      flight.arrivalTime,
                      flight.arrival?.timeZone,
                    )}`
                  : ''}
              </Typography>
            )}
            {!isArrival && flight.departure?.terminal && (
              <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                Terminal {flight.departure.terminal}
                {flight.departureTime
                  ? ` · departs ${formatFlightTime(
                      flight.departureTime,
                      flight.departure?.timeZone,
                    )}`
                  : ''}
              </Typography>
            )}
          </Alert>
        )}
        <Input
          name={`${namePrefix}.specialInstructions`}
          label="Special Instructions"
          disabled={disabled}
          multiline
          minRows={5}
          maxRows={5}
          inputProps={{ maxLength: 2000 }}
        />
      </Box>
    </ConnectForm>
  )
}

export default AdditionalServices
